const SNAPSHOT_VERSION = 1;
const DEFAULT_HISTORY_LIMIT = 3600;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function assertFiniteNonNegative(value, name) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
}

export function computeSimulationStepChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    throw new TypeError('snapshot must be an object');
  }

  const payload = clone(snapshot);
  delete payload.checksum;
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;

  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export class SimulationStepSnapshotStore {
  constructor({ historyLimit = DEFAULT_HISTORY_LIMIT } = {}) {
    if (!Number.isInteger(historyLimit) || historyLimit <= 0) {
      throw new RangeError('historyLimit must be a positive integer');
    }

    this.historyLimit = historyLimit;
    this.history = [];
    this.nextTick = 0;
  }

  capture({
    stepSeconds,
    simulationTime,
    input,
    replay = null,
    randomState = null,
    worldStateRef = null,
    metadata = {},
  }) {
    assertPositiveFinite(stepSeconds, 'stepSeconds');
    assertFiniteNonNegative(simulationTime, 'simulationTime');
    if (!input || typeof input !== 'object') {
      throw new TypeError('input must be an object');
    }

    const payload = {
      version: SNAPSHOT_VERSION,
      tick: this.nextTick,
      stepSeconds,
      simulationTime,
      input: clone(input),
      replay: clone(replay),
      randomState: clone(randomState),
      worldStateRef: clone(worldStateRef),
      metadata: clone(metadata) || {},
    };
    const snapshot = deepFreeze({
      ...payload,
      checksum: computeSimulationStepChecksum(payload),
    });

    this.history.push(snapshot);
    if (this.history.length > this.historyLimit) this.history.shift();
    this.nextTick += 1;
    return snapshot;
  }

  getLatest() {
    return this.history.at(-1) || null;
  }

  get(tick) {
    if (!Number.isInteger(tick) || tick < 0) {
      throw new RangeError('tick must be a non-negative integer');
    }
    return this.history.find((snapshot) => snapshot.tick === tick) || null;
  }

  list({ fromTick = 0, limit = this.historyLimit } = {}) {
    if (!Number.isInteger(fromTick) || fromTick < 0) {
      throw new RangeError('fromTick must be a non-negative integer');
    }
    if (!Number.isInteger(limit) || limit <= 0) {
      throw new RangeError('limit must be a positive integer');
    }
    return this.history
      .filter((snapshot) => snapshot.tick >= fromTick)
      .slice(0, limit);
  }

  verify(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
      return { ok: false, actualChecksum: null, expectedChecksum: null };
    }

    const actualChecksum = typeof snapshot.checksum === 'string' ? snapshot.checksum : null;
    const expectedChecksum = computeSimulationStepChecksum(snapshot);
    return {
      ok: actualChecksum === expectedChecksum,
      actualChecksum,
      expectedChecksum,
    };
  }

  reset({ nextTick = 0 } = {}) {
    if (!Number.isInteger(nextTick) || nextTick < 0) {
      throw new RangeError('nextTick must be a non-negative integer');
    }
    this.history = [];
    this.nextTick = nextTick;
    return this.getStatus();
  }

  getStatus() {
    return {
      version: SNAPSHOT_VERSION,
      historyLimit: this.historyLimit,
      retained: this.history.length,
      nextTick: this.nextTick,
      oldestTick: this.history[0]?.tick ?? null,
      newestTick: this.history.at(-1)?.tick ?? null,
    };
  }
}
